import { Link } from 'react-router-dom';
import { FiClock, FiExternalLink } from 'react-icons/fi';
import CopyButton from '../components/transaction/CopyButton';
import SponsoredSection from '../components/SponsoredSection';
import { useSolanaBlock } from '../hooks/useSolanaBlock';
import { formatRelativeTime, formatUtcTimestamp } from '../utils/formatTime';

function DetailRow({ label, children }) {
  return (
    <div className="stx-row">
      <div className="stx-row__label">{label}</div>
      <div className="stx-row__value">{children}</div>
    </div>
  );
}

function SolanaBlockDetailsView({ slot }) {
  const { data, loading, error } = useSolanaBlock(slot);

  if (loading) {
    return (
      <main className="sections stx-page">
        <div className="stx-skeleton stx-skeleton-title" />
        <div className="stx-skeleton stx-skeleton-panel" />
      </main>
    );
  }

  if (error || !data) {
    return (
      <main className="sections stx-page">
        <h1 className="stx-page-title">Block Details</h1>
        <div className="stx-error">
          <p>{error || 'Block not found.'}</p>
          <Link to="/" className="stx-link">Back to home</Link>
        </div>
      </main>
    );
  }

  const explorerUrl = `https://solscan.io/block/${data.slot}`;

  return (
    <main className="sections stx-page">
      <SponsoredSection />
      <h1 className="stx-page-title">Block Details</h1>
      <p className="stx-block-subtitle">Solana slot {data.slot}</p>

      <section className="stx-card">
        <DetailRow label="Slot">
          <div className="stx-inline-group">
            <span className="stx-link stx-link--bold">{data.slot}</span>
            <CopyButton text={String(data.slot)} />
            <a href={explorerUrl} target="_blank" rel="noreferrer" className="stx-btn stx-btn--outline">
              View on Solscan
              <FiExternalLink aria-hidden />
            </a>
          </div>
        </DetailRow>

        <DetailRow label="Block Time">
          <div className="stx-inline-group">
            <span className="stx-muted stx-time-ago">
              <FiClock aria-hidden />
              {formatRelativeTime(data.blockTime)}
            </span>
            <span className="stx-muted">{formatUtcTimestamp(data.blockTime)}</span>
          </div>
        </DetailRow>

        <DetailRow label="Blockhash">
          <div className="stx-hash-line">
            <code className="stx-mono">{data.blockhash || '—'}</code>
            {data.blockhash ? <CopyButton text={data.blockhash} /> : null}
          </div>
        </DetailRow>

        <DetailRow label="Parent Slot">
          {data.parentSlot != null ? (
            <Link to={`/tx/${data.parentSlot}`} className="stx-link stx-link--bold">
              {data.parentSlot}
            </Link>
          ) : (
            <span>—</span>
          )}
        </DetailRow>

        <DetailRow label="Block Height">
          <span>{data.blockHeight != null ? data.blockHeight.toLocaleString() : '—'}</span>
        </DetailRow>

        <DetailRow label="Transactions">
          <span>{data.transactionCount?.toLocaleString() ?? '—'}</span>
        </DetailRow>
      </section>

      {data.transactions?.length > 0 ? (
        <section className="stx-card stx-card--tx-list">
          <h2 className="stx-card-heading">Transactions in this block</h2>
          <div className="stx-block-tx-list">
            {data.transactions.map((tx) => (
              <Link
                key={tx.signature}
                to={`/tx/${encodeURIComponent(tx.signature)}`}
                className="stx-block-tx-item"
              >
                <code>{tx.displaySig}</code>
              </Link>
            ))}
          </div>
          {data.transactionCount > data.transactions.length ? (
            <p className="stx-muted stx-block-tx-more">
              Showing {data.transactions.length} of {data.transactionCount.toLocaleString()} transactions.
            </p>
          ) : null}
        </section>
      ) : null}
    </main>
  );
}

export default SolanaBlockDetailsView;
